import { injectStrict } from '@/injection/injectStrict.ts';
import { DocumentDownloaderKey } from '@/injection/keys.ts';

import { ApiRequestParams } from '@/types/api.type.ts';
import {
    AccountingReportDownloadErrors,
    AccountingReportDownloadErrorsSchema,
    AccountingReportRequestFilter,
    ReportAuctionsForPeriodDownloadErrors,
    ReportAuctionsForPeriodDownloadErrorsSchema,
    ReportAuctionsForPeriodRequestFilter,
    ReportAuctionsRequestFilter,
} from '@/types/report.type.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useDocumentDownloader() {
    const $DocumentDownloader = injectStrict(DocumentDownloaderKey);

    const downloadCarDocument = (id: number, name: string): void => {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        $DocumentDownloader.download('GetCarDocumentData', requestParams, name);
    };

    const downloadAuctionInvoice = (id: number, name: string): void => {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        $DocumentDownloader.download('GetAuctionInvoiceFile', requestParams, name);
    };

    const downloadAuctionSaleContract = (id: number, name: string): void => {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        $DocumentDownloader.download('GetAuctionSaleContractFile', requestParams, name);
    };

    const downloadBidReports = (name: string, requestFilter?: ReportAuctionsRequestFilter): Promise<void> => {
        const requestParams: ApiRequestParams = {
            query: requestFilter,
        };

        return $DocumentDownloader.download('GetBidReportAuctionsFile', requestParams, name);
    };

    const downloadBidReport = (id: number, name: string): Promise<void> => {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return $DocumentDownloader.download('GetBidReportByAuctionFile', requestParams, name);
    };

    const downloadCarPlacementSaleReport = (id: number, name: string): Promise<void> => {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return $DocumentDownloader.download('GetCarsPostingAndSellingReportFile', requestParams, name);
    };

    const downloadAccountingReport = (name: string, requestFilter?: AccountingReportRequestFilter): Promise<void> => {
        const requestParams: ApiRequestParams = {
            query: requestFilter,
        };

        return new Promise((resolve, reject) => {
            $DocumentDownloader
                .download('GetAccountingReportFile', requestParams, name)
                .then(resolve)
                .catch(({ response: { data } }: any) => {
                    if (data instanceof Blob) {
                        data.text().then((json) => {
                            const data: any = JSON.parse(json);

                            if (!(data?.errors)) {
                                reject({});
                            }

                            const parsedErrors = AccountingReportDownloadErrorsSchema.parse(data.errors);
                            const errors: AccountingReportDownloadErrors = parsedErrors as unknown as AccountingReportDownloadErrors;

                            reject(errors);
                        });
                    }
                });
        });
    };

    const downloadDocument = (id: number, name: string): Promise<void> => {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return $DocumentDownloader.download('GetDocumentData', requestParams, name);
    };

    const downloadAuctionsReport = (name: string, requestFilter?: ReportAuctionsForPeriodRequestFilter): Promise<void> => {
        const requestParams: ApiRequestParams = {
            query: requestFilter,
        };

        return new Promise((resolve, reject) => {
            $DocumentDownloader
                .download('GetAuctionsReportFile', requestParams, name)
                .then(resolve)
                .catch(({ response: { data } }: any) => {
                    if (data instanceof Blob) {
                        data.text().then((json) => {
                            const data: any = JSON.parse(json);

                            if (!(data?.errors)) {
                                reject({});
                            }

                            const parsedErrors = ReportAuctionsForPeriodDownloadErrorsSchema.parse(data.errors);
                            const errors: ReportAuctionsForPeriodDownloadErrors = parsedErrors as unknown as ReportAuctionsForPeriodDownloadErrors;

                            reject(errors);
                        });
                    }
                });
        });
    };

    return {
        downloadCarDocument: downloadCarDocument,
        downloadAuctionInvoice: downloadAuctionInvoice,
        downloadAuctionSaleContract: downloadAuctionSaleContract,
        downloadBidReports: downloadBidReports,
        downloadBidReport: downloadBidReport,
        downloadCarPlacementSaleReport: downloadCarPlacementSaleReport,
        downloadAccountingReport: downloadAccountingReport,
        downloadDocument: downloadDocument,
        downloadAuctionsReport: downloadAuctionsReport,
    };
}
